import { useState } from "react";
import { KeyRound, Plus } from "lucide-react";
import {
  Page,
  Card,
  ChangePasswordModal,
  DescriptionList,
  Table,
  type Column,
  Button,
  Badge,
  Text,
  DateTimeField,
  EmptyState,
} from "@marekl/adminer/components";
import { notifySuccess } from "@marekl/adminer/api";
import { useAuth, useIsAdmin } from "../../auth/useAuth";
import type { User } from "../../auth/queries";
import { useUserList } from "../../lib/queries/users";
import { useAllCompanyAccess } from "../../lib/queries/companies";
import { companyAccessSummary } from "./CompanyAccessFields";
import { CreateUserModal, EditUserModal, RoleBadge } from "./UserModals";
import styles from "./SettingsPage.module.css";

/**
 * "Můj účet": the lib's ChangePasswordModal drives `useChangePassword`; only
 * the length floor is the app's (backend policy is 10, the lib default is 8).
 */
function AccountTab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <>
      <DescriptionList>
        <DescriptionList.Item label="Uživatelské jméno">{user?.username}</DescriptionList.Item>
        <DescriptionList.Item label="Role">{user && <RoleBadge role={user.role} />}</DescriptionList.Item>
      </DescriptionList>
      <div className={styles.actions}>
        <Button variant="primary" startIcon={<KeyRound size={16} aria-hidden="true" />} onClick={() => setOpen(true)}>
          změnit heslo
        </Button>
        <Text variant="muted">Ostatní přihlášení se poté odhlásí.</Text>
      </div>
      <ChangePasswordModal
        open={open}
        minLength={10}
        onClose={() => setOpen(false)}
        onSuccess={() => {
          setOpen(false);
          notifySuccess("Heslo bylo změněno. Ostatní přihlášení byla odhlášena.");
        }}
      />
    </>
  );
}

type UserModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; user: User };

/** "Uživatelé" (admin): the instance's users; "+ nový" sits in the card's header. */
function UsersTab({ modal, setModal }: { modal: UserModalState; setModal: (m: UserModalState) => void }) {
  const { user: me } = useAuth();
  const users = useUserList();
  const access = useAllCompanyAccess(true);

  const columns: Column<User>[] = [
    {
      key: "username",
      header: "Uživatelské jméno",
      primary: true,
      render: (row) => row.username,
    },
    {
      key: "role",
      header: "Role",
      render: (row) => <RoleBadge role={row.role} />,
    },
    {
      key: "companies",
      header: "Firmy",
      render: (row) =>
        row.role === "admin" ? <Text variant="muted">všechny</Text> : companyAccessSummary(access.companies, access.byUser[row.id]) || <Text variant="muted">žádná</Text>,
    },
    {
      key: "active",
      header: "Stav",
      render: (row) =>
        row.active ? (
          <Badge weight="ghost" variant="ok" dot>Aktivní</Badge>
        ) : (
          <Badge weight="ghost" variant="muted" dot>Neaktivní</Badge>
        ),
    },
    {
      key: "created_at",
      header: "Vytvořen",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action disabled={access.isLoading} onClick={() => setModal({ kind: "edit", user: row })}>
            Upravit
          </Table.Action>
        </Table.Actions>
      ),
    },
  ];

  return (
    <>
      <Table<User>
        cards
        columns={columns}
        rows={users.data ?? []}
        rowKey={(r) => r.id}
        isLoading={users.isLoading}
        error={users.error}
        onRetry={users.refetch}
        empty={<EmptyState title="Žádní uživatelé" />}
      />
      {modal.kind === "create" && <CreateUserModal companies={access.companies} onClose={() => setModal({ kind: "closed" })} />}
      {modal.kind === "edit" && (
        <EditUserModal
          user={modal.user}
          isSelf={modal.user.id === me?.id}
          companies={access.companies}
          access={access.byUser[modal.user.id] ?? {}}
          onClose={() => setModal({ kind: "closed" })}
        />
      )}
    </>
  );
}

/**
 * "Uživatelé a účet": one card with tabs, like the company detail — the
 * signed-in user's account, and for an admin the instance's users.
 */
export function SettingsPage() {
  const isAdmin = useIsAdmin();
  const { user } = useAuth();
  const [tab, setTab] = useState("account");
  const [modal, setModal] = useState<UserModalState>({ kind: "closed" });
  const tabs = [{ id: "account", label: "Můj účet" }, ...(isAdmin ? [{ id: "users", label: "Uživatelé" }] : [])];
  const usersTab = tab === "users" && isAdmin;

  return (
    <Page>
      <Page.Content>
        <Card>
          <Card.Header
            title="Uživatelé a účet"
            subtitle={user?.username}
            actions={
              usersTab && (
                <Button size="sm" variant="primary" startIcon={<Plus size={14} aria-hidden="true" />} onClick={() => setModal({ kind: "create" })}>
                  nový
                </Button>
              )
            }
          />
          {tabs.length > 1 && <Card.Tabs tabs={tabs} value={tab} onChange={setTab} aria-label="Uživatelé a účet" />}
          {usersTab ? (
            <Card.Content padding="none">
              <UsersTab modal={modal} setModal={setModal} />
            </Card.Content>
          ) : (
            <Card.Content>
              <AccountTab />
            </Card.Content>
          )}
        </Card>
      </Page.Content>
    </Page>
  );
}
